import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Row,
  Spinner,
} from "reactstrap";
import Select from "react-select";
import { DEFAULT_PAGE_SIZE, pascalToCamel, phoneNumberShowFormat } from "helpers/GeneralHelper";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

import CommonTableForCandidate from "components/Common/CandidateTableContainer";
import LegacyFilterToolbar, {
  LEGACY_FILTER_INPUT_CLASS,
} from "components/Common/LegacyFilterToolbar";
import { AsyncClientSelect, AsyncSourceSelect } from "components/Common/AsyncSearchSelects";
import {
  applyClientAsyncFilterOptionToFormState,
  buildClientAsyncFilterInitialOption,
  getClientAsyncFilterLabelFieldKey,
} from "helpers/metadataFilterHelpers";
import {
  getVerifyTestMetadata,
  submitVerifyTestFilterData,
  approveVerifyTest,
  cancelVerifyTest,
} from "model/VerifyTestModel";
import { fetchCommonOrderStatusOptions } from "model/CandidateModel";
import { useCandidateTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";
import Loaders from "components/Common/Loaders";
import { useCommonFilterOptions } from "libs/context/CommonFilterOptionsContext";

const selectZIndexStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
};

const defaultTableColumns = [
  { Header: "Id", accessor: "id" },
    { Header: "Name", accessor: "name" },
    { Header: "Status", accessor: "status" },
  ];
  
const strOrNull = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s === "" ? null : s;
};

const normalizeVerifyTestFilterPayload = (raw = {}) => {
  const out = { ...raw };
  ["nameContains", "emailContains", "orderStatus", "clientId", "sourceId"].forEach((key) => {
    if (key in out) out[key] = strOrNull(out[key]);
  });
  return out;
};

const getSelectedOption = (options, value) =>
  (Array.isArray(options) ? options : []).find(
    (opt) => String(opt?.value ?? "") === String(value ?? "")
  ) || null;

const noop = () => {};

const VerifyTest = () => {
  const { fetchMetadataSchema } = useCommonFilterOptions();
  const { pageSize: settingsPageSize, settingsReady } =
    useCandidateTableSettingsPageSize("VerifyTest");
  const initialFilterFetchedRef = useRef(false);
  const [metadataBootstrapDone, setMetadataBootstrapDone] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableMetaData, setTableMetaData] = useState([]);
  const [orderStatusOptions, setOrderStatusOptions] = useState([]);
  /** Upper filter bar (client-register style). */
  const [filterForm, setFilterForm] = useState({
    search: "",
    orderStatus: "",
    clientId: "",
    [getClientAsyncFilterLabelFieldKey()]: "",
    sourceId: "",
  });

  const [requestPayload, setRequestPayload] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    nameContains: null,
    emailContains: null,
    orderStatus: null,
    clientId: null,
    sourceId: null,
  });

  const [paginationMeta, setPaginationMeta] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalRecords: 0,
  });

  const getVerifyTestList = useCallback(
    async (payloadOverrides = {}, options = {}) => {
      const { onlyPagination = false } = options;
      setLoading(true);
      try {
        const payload = onlyPagination
          ? {
              pageNumber: Number(payloadOverrides.pageNumber) || 1,
              pageSize: Number(payloadOverrides.pageSize) || DEFAULT_PAGE_SIZE,
            }
          : normalizeVerifyTestFilterPayload({
              pageNumber: requestPayload.pageNumber,
              pageSize: requestPayload.pageSize,
              nameContains: requestPayload.nameContains,
              emailContains: requestPayload.emailContains,
              orderStatus: requestPayload.orderStatus,
              clientId: requestPayload.clientId,
              sourceId: requestPayload.sourceId,
              ...payloadOverrides,
            });

        const response = await submitVerifyTestFilterData(payload);
        const responseData = response?.data ?? response ?? {};
        const listData =
          responseData?.records ??
          responseData?.data ??
          (Array.isArray(responseData) ? responseData : []);

        const records = Array.isArray(listData) ? listData : [];
        const mapped = records.map((item = {}) => {
          const formattedPhone = phoneNumberShowFormat(
            item?.candidatePhone ?? item?.phone ?? item?.phoneNumber ?? ""
          );
          return {
            ...item,
            id: item?.id ?? item?._id ?? "",
            candidatePhone: formattedPhone,
          };
        });
        setRows(mapped);

        if (response == null || response?.success !== false) {
          setPaginationMeta({
            pageNumber: Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
            pageSize: Number(responseData?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
            totalPages: Number(responseData?.totalPages) || 0,
            totalRecords: Number(responseData?.totalRecords) || records.length,
          });
          if (onlyPagination) {
            setRequestPayload({
              pageNumber: payload.pageNumber,
              pageSize: payload.pageSize,
              nameContains: null,
              emailContains: null,
              orderStatus: null,
              clientId: null,
              sourceId: null,
            });
            setFilterForm({
              search: "",
              orderStatus: "",
              clientId: "",
              [getClientAsyncFilterLabelFieldKey()]: "",
              sourceId: "",
            });
          } else {
            setRequestPayload(normalizeVerifyTestFilterPayload(payload));
          }
        } else {
          setRows([]);
          if (response?.message) {
            toast.error(response.message);
          }
        }
      } catch (err) {
        toast.error(err?.message || "Unable to load verify test list");
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [requestPayload]
  );

  useEffect(() => {
    const bootstrap = async () => {
      setLoading(true);
      try {
        const metaRes = await fetchMetadataSchema("metadata:verifyTest", () => getVerifyTestMetadata());
        const meta = metaRes?.data ?? metaRes ?? {};

        const cols = meta?.table?.columns?.length
          ? meta.table.columns.map((col) => ({
              Header: col?.title,
              accessor: pascalToCamel(col?.field),
              type: col?.type,
            }))
          : defaultTableColumns;
        setTableMetaData(cols);
      } catch (e) {
        toast.error(e?.message || "Unable to load verify test metadata");
        setTableMetaData(defaultTableColumns);
      }

      try {
        const orderStatusRes = await fetchCommonOrderStatusOptions();
        const records = orderStatusRes?.data || [];
        setOrderStatusOptions(
          (Array.isArray(records) ? records : [])
            .map((item) => ({
              value: String(item?.value || "").trim(),
              label: item?.data || item?.value || "Order Status",
            }))
            .filter((opt) => opt?.value)
        );
      } catch (e) {
        setOrderStatusOptions([]);
      }

      setMetadataBootstrapDone(true);
    };
    bootstrap();
  }, []);

  /** 1) settings page size ready → 2) call filter API once */
  useEffect(() => {
    if (!settingsReady || !metadataBootstrapDone || initialFilterFetchedRef.current) return;
    initialFilterFetchedRef.current = true;
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
    setRequestPayload((previous) => ({ ...previous, pageSize }));
    setPaginationMeta((previous) => ({ ...previous, pageSize }));
    void getVerifyTestList({ pageNumber: 1, pageSize }, { onlyPagination: true });
  }, [settingsReady, settingsPageSize, metadataBootstrapDone, getVerifyTestList]);

  const buildPayloadFromUpperForm = (form = {}) => {
    const search = String(form?.search || "").trim();
    return normalizeVerifyTestFilterPayload({
      pageNumber: 1,
      pageSize: requestPayload.pageSize || DEFAULT_PAGE_SIZE,
      nameContains: strOrNull(search),
      emailContains: strOrNull(search),
      orderStatus: strOrNull(form?.orderStatus),
      clientId: strOrNull(form?.clientId),
      sourceId: strOrNull(form?.sourceId),
    });
  };

  const handleUpperSearch = () => {
    getVerifyTestList(buildPayloadFromUpperForm(filterForm));
  };

  const handlePageChange = async (page) => {
    await getVerifyTestList({ pageNumber: page });
  };

  const handleRowsPerPageChange = async (rows) => {
    await getVerifyTestList({ pageNumber: 1, pageSize: rows });
  };

  const getRowId = (row = {}) => row?.id ?? row?._id ?? "";

  const handleApprove = async (row = {}) => {
    const id = getRowId(row);
    if (!id) {
      toast.error("Record ID not found");
      return;
    }
    const result = await Swal.fire({
      title: "Approve",
      text: "Approve this verify test record?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "OK",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;

    setLoading(true);
    try {
      const res = await approveVerifyTest(id);
      if (res?.success || res?.isSuccess || res?.status) {
        toast.success(res?.message || "Approved successfully");
        await getVerifyTestList({});
      } else {
        toast.error(res?.message || res?.error?.message || "Unable to approve");
      }
    } catch (e) {
      toast.error(e?.message || "Unable to approve");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (row = {}) => {
    const id = getRowId(row);
    if (!id) {
      toast.error("Record ID not found");
      return;
    }
    const result = await Swal.fire({
      title: "Cancel",
      text: "Cancel this verify test record?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "OK",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;

    setLoading(true);
    try {
      const res = await cancelVerifyTest(id);
      if (res?.success || res?.isSuccess || res?.status) {
        toast.success(res?.message || "Cancelled successfully");
        await getVerifyTestList({});
      } else {
        toast.error(res?.message || res?.error?.message || "Unable to cancel");
      }
    } catch (e) {
      toast.error(e?.message || "Unable to cancel");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-content verify-test-page">
        <Container fluid>
      <Card>
        <CardBody>
            <h5 className="fw-bold mb-3">Verify Test</h5>
            <hr />

            <LegacyFilterToolbar>
            <Row className="metadata-filter-toolbar__row g-2 g-md-3 align-items-end">
              <Col xs="12" md="4" className="metadata-filter-field metadata-filter-field--text">
                <span className="manage-user-filter-label">Search</span>
                <div className="metadata-filter-field__control">
                <Input
                  type="text"
                  name="search"
                  placeholder="Search by Email/Company"
                  className={LEGACY_FILTER_INPUT_CLASS}
                  value={filterForm.search}
                  onChange={(e) => {
                    const nextValue = e?.target?.value || "";
                    setFilterForm((prev) => ({ ...prev, search: nextValue }));
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleUpperSearch();
                    }
                  }}
                />
                </div>
              </Col>
              <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--dropdown">
                <span className="manage-user-filter-label">Order Status</span>
                <div className="metadata-filter-field__control">
                <Select
                  options={orderStatusOptions}
                  className="select2-selection"
                  placeholder="Select Order Status"
                  isClearable
                  value={getSelectedOption(orderStatusOptions, filterForm.orderStatus)}
                  onChange={(option) =>
                    setFilterForm((prev) => ({
                      ...prev,
                      orderStatus: option?.value ?? "",
                    }))
                  }
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  styles={selectZIndexStyles}
                  menuPosition="fixed"
                />
                </div>
              </Col>
              <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--autocomplete">
                <span className="manage-user-filter-label">Client</span>
                <div className="metadata-filter-field__control">
                <AsyncClientSelect
                  key={`verify-test-client-${filterForm.clientId}`}
                  pageName="VerifyTest"
                  value={filterForm.clientId}
                  initialOption={buildClientAsyncFilterInitialOption(
                    filterForm.clientId,
                    filterForm[getClientAsyncFilterLabelFieldKey()]
                  )}
                  onChange={(option) =>
                    setFilterForm((prev) => applyClientAsyncFilterOptionToFormState(prev, option))
                  }
                  placeholder="Select Client"
                />
                </div>
              </Col>
              <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--autocomplete">
                <span className="manage-user-filter-label">Source</span>
                <div className="metadata-filter-field__control">
                <AsyncSourceSelect
                  value={filterForm.sourceId}
                  onChange={(option) =>
                    setFilterForm((prev) => ({
                      ...prev,
                      sourceId: option?.value ?? "",
                    }))
                  }
                  placeholder="Select Source"
                />
                </div>
              </Col>
              <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--actions">
                <span className="manage-user-filter-label" aria-hidden="true">&nbsp;</span>
                <div className="metadata-filter-toolbar__actions">
                <Button color="primary" className="w-100 manage-user-search-btn" type="button" onClick={handleUpperSearch}>
                  <i className="fa fa-search me-1" />
                  Search
                </Button>
                </div>
              </Col>
            </Row>
            </LegacyFilterToolbar>

            {loading ? (
              // <div
              //   className="d-flex justify-content-center align-items-center py-5 spinner-card-height"              >
              //   <Spinner color="primary" />
              // </div>
              <Loaders />
            ) : tableMetaData?.length ? (
              <CommonTableForCandidate
                columns={tableMetaData}
                data={rows}
            isGlobalFilter={false}
            isSort={true}
            isActionRequired={true}
            tableClass="align-middle table-nowrap mb-0"
            theadClass="table-light"
                pageName="VerifyTest"
                isCheckboxLeftSide={false}
                isPagination={true}
                currentPage={paginationMeta?.pageNumber}
                rowsPerPage={paginationMeta?.pageSize}
                totalPages={paginationMeta?.totalPages}
                totalRecords={paginationMeta?.totalRecords}
                onPageChange={handlePageChange}
                onPageSizeChange={handleRowsPerPageChange}
                setCommonFilterModalOpen={noop}
                setCommonResetFilter={noop}
                allCommonFilter={false}
                onVerifyTestApprove={handleApprove}
                onVerifyTestCancel={handleCancel}
              />
            ) : (
              <div className="text-muted text-center py-5">Unable to load table columns.</div>
            )}
        </CardBody>
      </Card>
      </Container>
      </div>
  );
};

VerifyTest.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(VerifyTest);
